import { Component, OnInit } from '@angular/core';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
    selectInAppNotificationsUser,
    selectUnreadInAppNotificationsTabCounters
} from 'src/app/in-app-notifications/store/selectors/in-app-notifications.selectors';
import {
    getInAppNotificationsUser,
    getUnreadInAppNotificationsTabCounters
} from 'src/app/in-app-notifications/store/actions/in-app-notifications.actions';
import { InAppNotificationsState } from 'src/app/in-app-notifications/store/in-app-notifications.state';
import { InAppNotificationType } from 'src/app/in-app-notifications/models/in-app-notification-type.enum';
import { InAppNotification } from 'src/app/in-app-notifications/models/in-app-notification.model';
import { KeyValuePair } from 'src/app/shared/models/key-value-pair.model';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { MatDialog } from '@angular/material/dialog';
import { InAppNotificationsPreferencesComponent } from 'src/app/in-app-notifications/in-app-notifications-preferences/in-app-notifications-preferences.component';
import { InAppNotificationsListComponent } from 'src/app/in-app-notifications/in-app-notifications-list/in-app-notifications-list.component';
import { Overlay } from '@angular/cdk/overlay';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { IdentityService } from 'src/app/core/identity.service';
import { CorporateDirectoryUser } from 'src/app/corporate-directory/models/corporate-directory-user.model';
import { ActivatedRoute } from '@angular/router';
import { DetailToolbarComponent } from 'src/app/shared/detail-toolbar.component';

export enum InAppNotificationTabLabel {
    Tasks = 'TASKS',
    Tickets = 'TICKETS',
    AMLiveList = 'LIVE LIST',
    Jobs = 'JOBS',
    Compliance = 'COMPLIANCE'
}

@UntilDestroy()
@Component({
    selector: 'nova-in-app-notifications-tabs',
    standalone: true,
    imports: [MatTabsModule, MatButtonModule, MatIconModule, InAppNotificationsListComponent, DetailToolbarComponent],
    templateUrl: './in-app-notifications-tabs.component.html',
    styleUrls: ['./in-app-notifications-tabs.component.scss']
})
export class InAppNotificationsTabsComponent implements OnInit {
    readonly flags = FeatureFlag;
    readonly InAppNotificationType = InAppNotificationType;
    selectedTabIndex = 0;
    inAppNotifications: InAppNotification[] = [];
    unreadInAppNotificationsTabCounters: Array<KeyValuePair<InAppNotificationType, number>> = [];
    hasAMSubmittalsPermission = false;
    enableInAppNotificationsTickets = false;
    enableComplianceListNotifications = false;

    aspNetUserId: string;
    currentUserId: string;
    user: CorporateDirectoryUser;

    currentTab = 'Tasks';

    get notificationsTitle(): string {
        if (this.currentUserId !== this.aspNetUserId) {
            if (this.user?.name?.length) {
                return `${this.user.name}'s Notifications`;
            }

            return 'Notifications';
        }

        return 'My Notifications';
    }

    constructor(
        private readonly _store: Store<InAppNotificationsState>,
        private readonly _dialog: MatDialog,
        private readonly _overlay: Overlay,
        private readonly _ldFeatureManager: LDFeatureManager,
        private readonly _identityService: IdentityService,
        private readonly _activatedRoute: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.currentUserId = this._identityService.current.userId;
        this._store
            .select(selectUnreadInAppNotificationsTabCounters)
            .pipe(untilDestroyed(this))
            .subscribe((data) => (this.unreadInAppNotificationsTabCounters = data));

        this._ldFeatureManager
            .isEnabled(this.flags.InAppNotificationsTickets)
            .pipe(untilDestroyed(this))
            .subscribe((enabled) => {
                this.enableInAppNotificationsTickets = enabled;
            });

        this._ldFeatureManager
            .isEnabled(this.flags.ComplianceInAppNotifications)
            .pipe(untilDestroyed(this))
            .subscribe((enabled) => {
                this.enableComplianceListNotifications = enabled;
            });

        this.hasAMSubmittalsPermission = this._identityService.hasPermission('accountmanagers', 'submittals');

        this.setupInAppNotificationData();
    }

    private setupInAppNotificationData() {
        this._activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((queryParams) => {
            const newUserId = queryParams.userId;
            if (!this.aspNetUserId || newUserId !== this.aspNetUserId) {
                this.loadData(newUserId ?? this.currentUserId);
            }
        });
    }

    loadData(userId: string) {
        this.aspNetUserId = userId ?? this.currentUserId;
        this._store.dispatch(getUnreadInAppNotificationsTabCounters({ userId: this.aspNetUserId }));

        if (this.aspNetUserId !== this.currentUserId) {
            this._store
                .select(selectInAppNotificationsUser)
                .pipe(untilDestroyed(this))
                .subscribe((data) => (this.user = data));
            this._store.dispatch(getInAppNotificationsUser({ userId: this.aspNetUserId }));
        }
    }

    get showAMLiveListNotifications(): boolean {
        return this.hasAMSubmittalsPermission;
    }

    getTabLabel(type: InAppNotificationType): string {
        const count = this.unreadInAppNotificationsTabCounters.find((x) => x.key === type);
        let label = '';
        switch (type) {
            case InAppNotificationType.Tasks:
                label = InAppNotificationTabLabel.Tasks;
                break;

            case InAppNotificationType.Tickets:
                label = InAppNotificationTabLabel.Tickets;
                break;

            case InAppNotificationType.AMLiveList:
                label = InAppNotificationTabLabel.AMLiveList;
                break;

            case InAppNotificationType.Jobs:
                label = InAppNotificationTabLabel.Jobs;
                break;

            case InAppNotificationType.Compliance:
                label = InAppNotificationTabLabel.Compliance;
                break;
        }

        return count?.value ? `${label} (${count.value})` : label;
    }

    onSelectedTabChange(event: MatTabChangeEvent) {
        this.setActiveTabForFlyout(event.tab.textLabel);
    }

    setActiveTabForFlyout(tabName: string): void {
        this.currentTab = tabName;
    }

    openPreferencesFlyout() {
        const tabData = {
            currentTab: this.currentTab
        };

        this._dialog.open(InAppNotificationsPreferencesComponent, {
            data: tabData,
            disableClose: true,
            panelClass: ['nova__dialog'],
            position: { top: '0px', right: '0px' },
            height: '100%',
            scrollStrategy: this._overlay.scrollStrategies.reposition()
        });
    }
}
